<script>
import { format, parseISO } from 'date-fns';
import { required, url } from '@vuelidate/validators';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import MultiselectDropdown from 'shared/components/ui/MultiselectDropdown.vue';
import HelperTextPopup from 'dashboard/components/ui/HelperTextPopup.vue';
import { isValidURL } from '../helper/URLHelper';
import { getRegexp } from 'shared/helpers/Validators';
import { useVuelidate } from '@vuelidate/core';
import { emitter } from 'shared/helpers/mitt';

import NextButton from 'dashboard/components-next/button/Button.vue';

const DATE_FORMAT = 'yyyy-MM-dd';

export default {
  components: {
    MultiselectDropdown,
    HelperTextPopup,
    NextButton,
  },
  props: {
    label: { type: String, required: true },
    icon: { type: String, default: '' },
    description: { type: String, default: '' },
    values: { type: Array, default: () => [] },
    value: { type: [String, Number, Boolean], default: '' },
    showActions: { type: Boolean, default: false },
    attributeType: { type: String, default: 'text' },
    attributeRegex: {
      type: String,
      default: null,
    },
    regexCue: { type: String, default: null },
    attributeKey: { type: String, required: true },
    contactId: { type: Number, default: null },
  },
  emits: ['update', 'delete', 'copy'],
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      isEditing: false,
      editedValue: null,
    };
  },
  computed: {
    displayValue() {
      if (this.isAttributeTypeDate) {
        return this.value
          ? new Date(this.value || new Date()).toLocaleDateString()
          : '---';
      }
      if (this.isAttributeTypeCheckbox) {
        return this.value === 'false' ? false : this.value;
      }
      return this.hasValue ? this.value : '---';
    },
    formattedValue() {
      return this.isAttributeTypeDate
        ? format(this.value ? new Date(this.value) : new Date(), DATE_FORMAT)
        : this.value;
    },
    listOptions() {
      return this.values.map((value, index) => ({
        id: index + 1,
        name: value,
      }));
    },
    selectedItem() {
      if (!this.hasValue) {
        return {};
      }
      const id = this.values.indexOf(this.editedValue) + 1;
      if (!id) {
        return {};
      }
      return { id, name: this.editedValue };
    },
    isAttributeTypeCheckbox() {
      return this.attributeType === 'checkbox';
    },
    isAttributeTypeList() {
      return this.attributeType === 'list';
    },
    isAttributeTypeLink() {
      return this.attributeType === 'link';
    },
    isAttributeTypeDate() {
      return this.attributeType === 'date';
    },
    hasValue() {
      return this.value !== null && this.value !== '';
    },
    valueTextClass() {
      if (this.isTechnicalAttribute) {
        return 'font-mono text-[12px] text-n-slate-11';
      }
      if (!this.hasValue) {
        return 'text-[13.5px] text-n-slate-11';
      }
      return 'text-[13.5px] text-n-slate-12';
    },
    // SITEUP: chave técnica do WhatsApp (JID/LID/Chat ID) — mono apagado,
    // valor se copia, não se lê
    isTechnicalAttribute() {
      return String(this.attributeKey || '').startsWith('whatsapp_');
    },
    urlValue() {
      return isValidURL(this.value) ? this.value : '---';
    },
    hrefURL() {
      return isValidURL(this.value) ? this.value : '';
    },
    notAttributeTypeCheckboxAndList() {
      return !this.isAttributeTypeCheckbox && !this.isAttributeTypeList;
    },
    inputType() {
      return this.isAttributeTypeLink ? 'url' : this.attributeType;
    },
    shouldShowErrorMessage() {
      return this.v$.editedValue.$error;
    },
    errorMessage() {
      if (this.v$.editedValue.url?.$invalid) {
        return this.$t('CUSTOM_ATTRIBUTES.VALIDATIONS.INVALID_URL');
      }
      if (this.v$.editedValue.regexValidation?.$invalid) {
        return (
          this.regexCue ||
          this.$t('CUSTOM_ATTRIBUTES.VALIDATIONS.INVALID_INPUT')
        );
      }
      return this.$t('CUSTOM_ATTRIBUTES.VALIDATIONS.REQUIRED');
    },
  },
  watch: {
    value() {
      this.isEditing = false;
      this.editedValue = this.formattedValue;
    },
    contactId() {
      // Fix to solve validation not resetting when contactId changes in contact page
      this.v$.$reset();
    },
  },

  validations() {
    if (this.isAttributeTypeLink) {
      return {
        editedValue: { required, url },
      };
    }
    return {
      editedValue: {
        required,
        regexValidation: value => {
          if (!this.attributeRegex || !value) return true;
          try {
            return getRegexp(this.attributeRegex).test(value);
          } catch {
            return false;
          }
        },
      },
    };
  },
  mounted() {
    this.editedValue = this.formattedValue;
    emitter.on(BUS_EVENTS.FOCUS_CUSTOM_ATTRIBUTE, this.onFocusAttribute);
  },
  unmounted() {
    emitter.off(BUS_EVENTS.FOCUS_CUSTOM_ATTRIBUTE, this.onFocusAttribute);
  },
  methods: {
    onFocusAttribute(focusAttributeKey) {
      if (this.attributeKey === focusAttributeKey) {
        this.onEdit();
      }
    },
    focusInput() {
      if (this.$refs.inputfield) {
        this.$refs.inputfield.focus();
      }
    },
    onClickAway() {
      this.v$.$reset();
      this.isEditing = false;
    },
    onEdit() {
      this.isEditing = true;
      this.$nextTick(() => {
        this.focusInput();
      });
    },
    onUpdateListValue(value) {
      if (value) {
        this.editedValue = value.name;
        this.onUpdate();
      }
    },
    onUpdate() {
      const updatedValue =
        this.attributeType === 'date'
          ? parseISO(this.editedValue)
          : this.editedValue;
      this.v$.$touch();
      if (this.v$.$invalid) {
        return;
      }
      this.isEditing = false;
      this.$emit('update', this.attributeKey, updatedValue);
    },
    onDelete() {
      this.isEditing = false;
      this.v$.$reset();
      this.$emit('delete', this.attributeKey);
    },
    onCopy() {
      this.$emit('copy', this.value);
    },
  },
};
</script>

<template>
  <!--
    Painel da conversa cabe ~300-360px (PWA 375px). Grade 7rem + valor
    apertava o rótulo e o (i) caía sozinho. Padrão único: rótulo numa linha
    (ícone + texto truncado + info, sem wrap), valor na linha de baixo.
  -->
  <div
    class="group/attr mb-2 py-0.5 last:mb-0"
    data-testid="custom-attribute-row"
  >
    <div class="flex items-start gap-2.5">
      <div
        class="flex min-w-0 flex-1 flex-col gap-0.5"
        data-testid="custom-attribute-grid"
      >
        <div
          class="flex min-w-0 items-center gap-1"
          data-testid="custom-attribute-label"
        >
          <div
            v-if="isAttributeTypeCheckbox"
            class="flex shrink-0 items-center"
          >
            <input
              v-model="editedValue"
              class="!my-0"
              type="checkbox"
              @change="onUpdate"
            />
          </div>
          <span
            v-if="icon"
            :class="icon"
            class="size-3.5 shrink-0 text-n-slate-11"
          />
          <span
            class="min-w-0 flex-1 truncate text-[12.5px] leading-snug"
            :class="
              v$.editedValue.$error ? 'text-n-ruby-11' : 'text-n-slate-11'
            "
            :title="label"
          >
            {{ label }}
          </span>
          <HelperTextPopup v-if="description" :message="description" />
        </div>

        <div
          v-if="notAttributeTypeCheckboxAndList"
          class="min-w-0 w-full"
          data-testid="custom-attribute-value"
        >
          <div v-if="isEditing" v-on-clickaway="onClickAway">
            <div class="mb-1 flex w-full items-center">
              <input
                ref="inputfield"
                v-model="editedValue"
                :type="inputType"
                class="!mb-0 !h-8 min-w-0 flex-1 !text-[13.5px] ltr:!rounded-r-none rtl:!rounded-l-none"
                autofocus="true"
                :class="{ error: v$.editedValue.$error }"
                @blur="v$.editedValue.$touch"
                @keyup.enter="onUpdate"
              />
              <div>
                <NextButton
                  sm
                  icon="i-ri-check-line"
                  class="h-[34px] ltr:rounded-l-none rtl:rounded-r-none"
                  @click="onUpdate"
                />
              </div>
            </div>
            <span
              v-if="shouldShowErrorMessage"
              class="-mt-px block w-full text-sm font-normal text-n-ruby-11"
            >
              {{ errorMessage }}
            </span>
          </div>
          <div
            v-show="!isEditing"
            class="flex min-w-0 items-center gap-1"
            :class="{ 'is-editable': showActions }"
          >
            <a
              v-if="isAttributeTypeLink"
              :href="hrefURL"
              target="_blank"
              rel="noopener noreferrer"
              :title="urlValue"
              class="mb-0 min-w-0 flex-1 truncate text-[13.5px] font-medium text-n-slate-12 hover:underline"
            >
              {{ urlValue }}
            </a>
            <p
              v-else
              :title="String(displayValue)"
              class="mb-0 min-w-0 flex-1 truncate font-medium"
              :class="valueTextClass"
            >
              {{ displayValue }}
            </p>
            <div class="flex shrink-0 items-center gap-0.5">
              <NextButton
                v-if="showActions && hasValue"
                v-tooltip="$t('CUSTOM_ATTRIBUTES.ACTIONS.COPY')"
                :aria-label="$t('CUSTOM_ATTRIBUTES.ACTIONS.COPY')"
                xs
                slate
                ghost
                icon="i-ph-clipboard"
                class="shrink-0 opacity-0 transition-opacity group-hover/attr:opacity-100 group-focus-within/attr:opacity-100 [@media(hover:none)]:opacity-100"
                @click="onCopy"
              />
              <NextButton
                v-if="showActions"
                v-tooltip.right="$t('CUSTOM_ATTRIBUTES.ACTIONS.EDIT')"
                :aria-label="$t('CUSTOM_ATTRIBUTES.ACTIONS.EDIT')"
                xs
                slate
                ghost
                icon="i-ph-pencil-simple"
                class="shrink-0 opacity-0 transition-opacity group-hover/attr:opacity-100 group-focus-within/attr:opacity-100 [@media(hover:none)]:opacity-100"
                @click="onEdit"
              />
            </div>
          </div>
        </div>

        <div
          v-if="isAttributeTypeList"
          class="min-w-0 w-full"
          data-testid="custom-attribute-list"
        >
          <MultiselectDropdown
            class="!mb-0 w-full"
            :options="listOptions"
            :selected-item="selectedItem"
            :has-thumbnail="false"
            :multiselector-placeholder="
              $t('CUSTOM_ATTRIBUTES.FORM.ATTRIBUTE_TYPE.LIST.PLACEHOLDER')
            "
            :no-search-result="
              $t('CUSTOM_ATTRIBUTES.FORM.ATTRIBUTE_TYPE.LIST.NO_RESULT')
            "
            :input-placeholder="
              $t(
                'CUSTOM_ATTRIBUTES.FORM.ATTRIBUTE_TYPE.LIST.SEARCH_INPUT_PLACEHOLDER'
              )
            "
            @select="onUpdateListValue"
          />
        </div>
      </div>
      <NextButton
        v-if="showActions && hasValue"
        v-tooltip.left="$t('CUSTOM_ATTRIBUTES.ACTIONS.DELETE')"
        :aria-label="$t('CUSTOM_ATTRIBUTES.ACTIONS.DELETE')"
        slate
        sm
        link
        icon="i-ph-trash"
        class="shrink-0 opacity-0 transition-opacity group-hover/attr:opacity-100 group-focus-within/attr:opacity-100 [@media(hover:none)]:opacity-100"
        @click="onDelete"
      />
    </div>
  </div>
</template>
